/* Phase 17.1 -- Bug report modal.
 *
 * Fired when the user clicks "Report bug" on an unknown error toast, or
 * imperatively via reportManualBug(). Captures the error snapshot + a
 * user note and POSTs to /api/support/bug-report. Mount this once at
 * the app root -- <BugReportModal /> in App.js.
 */
import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Bug, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useBugReporter, closeBugReporter } from "@/lib/errorToast";
import { MODAL_SPRING } from "@/lib/motion";
import errorToast from "@/lib/errorToast";

export function BugReportModal() {
  const bug = useBugReporter();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!bug;

  // Escape closes -- roadmap A.1 accessibility fix.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeBugReporter();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        error_text: bug?.extracted?.text || "",
        error_kind: bug?.extracted?.kind || "unknown",
        http_status: bug?.extracted?.status || null,
        user_note: note.trim() || null,
        context: bug?.context || {},
      };
      await api.post("/support/bug-report", payload);
      toast.success("Report sent. Our team will take a look.");
      setNote("");
      closeBugReporter();
    } catch (e) {
      errorToast(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="bug-report-modal"
          onClick={closeBugReporter}
        >
          <motion.div
            {...MODAL_SPRING}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-[#0a1526]/95 border border-white/15 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center">
                  <Bug className="h-4 w-4 text-amber-300" />
                </div>
                <div>
                  <div className="font-heading text-sm uppercase tracking-[0.08em]">
                    Report a bug
                  </div>
                  <div className="text-[11px] text-foreground/60">
                    We'll capture the current page state and recent API calls.
                  </div>
                </div>
              </div>
              <button
                onClick={closeBugReporter}
                data-testid="bug-report-close"
                className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-white/5"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                <div className="text-[10px] uppercase tracking-[0.14em] text-foreground/55 mb-1">
                  Error
                </div>
                <div className="text-sm text-foreground/85 break-words" data-testid="bug-report-error-text">
                  {bug?.extracted?.text || "—"}
                </div>
                {bug?.extracted?.status && (
                  <div className="text-[11px] text-foreground/50 mt-1">
                    HTTP {bug.extracted.status}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-foreground/55">
                  What were you trying to do? (optional)
                </label>
                <textarea
                  data-testid="bug-report-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="I was trying to save an employee..."
                  className="mt-1 w-full text-sm bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 focus:border-sky-400/60 focus:outline-none resize-none"
                />
              </div>
              <div className="text-[10px] text-foreground/50">
                Attached: current route, timestamp, last 5 API calls, non-sensitive settings.
              </div>
            </div>

            <div className="p-4 pt-0 flex gap-2">
              <Button
                variant="ghost"
                onClick={closeBugReporter}
                data-testid="bug-report-cancel"
                className="flex-1 text-foreground/70"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={busy}
                data-testid="bug-report-submit"
                className="flex-1 bg-gradient-to-br from-sky-500 to-blue-700 text-white"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-1.5" />
                    Send report
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default BugReportModal;
